import React from 'react';
import type { TCredentialingOverview, TMalpracticeAlert } from 'types/credentialing';
import { malpracticeExpiryText } from 'pages/PPACredentialingStartPage/malpracticeExpiry';
import { ArrowSm, WarnIcon } from '../SetupReminderBanner';
import s from '../SetupReminderBanner/s.module.less';

type TEntry = {
    providerId?: number;
    name: string;
    self: boolean;
    alert: TMalpracticeAlert;
};

type Props = {
    overview: TCredentialingOverview;
    currentProviderId?: number;
    onUpdate: (providerId?: number) => void;
};

const isShown = (a?: TMalpracticeAlert | null): a is TMalpracticeAlert => !!a && a.level !== 'NONE';

// 最紧急的排前面：已过期优先，其次剩余天数少的
const urgency = (e: TEntry) => e.alert.daysUntilExpiry ?? Number.MAX_SAFE_INTEGER;

// 原型 malpracticeStatusBannerHTML（10/15）：与 fax / payout 提醒同款的页面级 banner，按钮
// 「Update certificate」打开 Settings 的 Malpractice tab。数据来自 overview：顶层 malpracticeAlert 是
// 登录 provider 自己的，providers[] 是 group 各成员的。原型只有单人文案；多人时（10/26 定）
// 列出所有快过期 / 已过期的成员，按钮跳到最紧急的那一位。有人已过期整条用红色。
export default function MalpracticeExpiryBanner({ overview, currentProviderId, onUpdate }: Props) {
    const entries: TEntry[] = [];
    if (isShown(overview.malpracticeAlert)) {
        entries.push({ providerId: currentProviderId, name: '', self: true, alert: overview.malpracticeAlert });
    }
    (overview.providers || []).forEach((p) => {
        if (!isShown(p.malpracticeAlert) || p.providerId === currentProviderId) return;
        entries.push({
            providerId: p.providerId,
            name: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email,
            self: false,
            alert: p.malpracticeAlert,
        });
    });
    if (!entries.length) return null;
    entries.sort((a, b) => urgency(a) - urgency(b));

    const anyExpired = entries.some((e) => e.alert.level === 'EXPIRED');
    const describe = (e: TEntry) =>
        malpracticeExpiryText(e.alert.level === 'EXPIRED', e.alert.daysUntilExpiry ?? 0, e.alert.expireDate || '');

    let text: React.ReactNode;
    if (entries.length === 1 && entries[0].self) {
        const only = entries[0];
        text =
            only.alert.level === 'EXPIRED'
                ? `Your malpractice certificate ${describe(
                      only,
                  )}. Every application still with a payer is on hold until you upload a current one.`
                : `Your malpractice certificate ${describe(
                      only,
                  )}. Payers put every open application on hold once it lapses, so upload the renewed certificate as soon as your carrier issues it.`;
    } else {
        text = (
            <>
                {entries.length === 1
                    ? 'Malpractice certificate needs attention: '
                    : 'Malpractice certificates need attention: '}
                {entries.map((e, i) => (
                    <React.Fragment key={e.providerId ?? 'self'}>
                        {i > 0 && ', '}
                        <b>{e.self ? 'You' : e.name}</b> ({describe(e)})
                    </React.Fragment>
                ))}
                . Every application still with a payer is on hold until a current certificate is uploaded.
            </>
        );
    }

    return (
        <div className={`${s.banner} ${anyExpired ? s.danger : s.warning}`}>
            <WarnIcon color={anyExpired ? '#DC2626' : undefined} />
            <span className={s.text}>{text}</span>
            <span className={s.actions}>
                <button type="button" className={s.action} onClick={() => onUpdate(entries[0].providerId)}>
                    Update certificate
                    <ArrowSm />
                </button>
            </span>
        </div>
    );
}
